import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SyntaxHighlighter from "react-syntax-highlighter";
import { atomOneDark } from "react-syntax-highlighter/dist/cjs/styles/hljs";
import { Helmet } from "react-helmet";
import PageHeader from "../Layouts/PageHeader";
import { UiIcon, CheckMark } from "../Layouts/Icon";
import uiIcons from "../Layouts/Icon/uiIcons";
import { copyTextToClipboard } from "../utils/formatters";
import {
  ICON_VIEWBOX,
  DUOTONE_OPACITY,
  componentName,
  toSvg,
  toDataUrl,
  toJsx,
  toVue,
  toSvelte,
  toAngular,
} from "../utils/svgIcon";
import { Link } from "react-router-dom";
import { COMPOSER_PATH } from "../constants";
import classes from "./examples.scss";

const DESCRIPTION =
  "Every way to use IcoFont: as a web font, as inline SVG or CSS, or as a React, Vue, Svelte or Angular component.";

// Code samples are built from real icons with the same exporters as the icon panel
const shapeOf = (key) => {
  const icon = uiIcons[key];
  const paths = [{ d: icon.path, opacity: 1 }];
  if (icon.pathSecondary) paths.push({ d: icon.pathSecondary, opacity: DUOTONE_OPACITY });
  return { name: key.replace(/^duotone:/, ""), isDuotone: !!icon.pathSecondary, viewBox: ICON_VIEWBOX, paths };
};

const HOME = shapeOf("home");
const DUO = shapeOf("duotone:add-users");

// Path data runs to hundreds of characters; shown shortened, copied in full
const shorten = (code) => code.replace(/ d="([^"]{20})[^"]*"/g, ' d="$1…"');

// ---- building blocks ---------------------------------------------------------

// Dark code block with a label and a Copy button that confirms in place
const CodeBlock = ({ code, label, language, display }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = () => {
    copyTextToClipboard(code);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className={classes.code}>
      <div className={classes.codeBar}>
        <span>{label}</span>
        <button
          type="button"
          className={`${classes.copy}${copied ? ` ${classes.copied}` : ""}`}
          onClick={copy}
          aria-label={`Copy ${label}`}
        >
          {copied ? (
            <React.Fragment>
              <CheckMark /> Copied
            </React.Fragment>
          ) : (
            "Copy"
          )}
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={atomOneDark}
        customStyle={{ margin: 0, padding: "16px 18px", background: "transparent", fontSize: 13, lineHeight: 1.6 }}
      >
        {display || code}
      </SyntaxHighlighter>
      <span className="sr-only" role="status">
        {copied ? `${label} copied` : ""}
      </span>
    </div>
  );
};

CodeBlock.propTypes = {
  code: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  language: PropTypes.string,
  display: PropTypes.string,
};

CodeBlock.defaultProps = { language: "html", display: "" };

// One use case: a linked title, a short explanation, then a live preview over its code
const Example = ({ id, title, children, preview, code }) => (
  <section className={classes.example} id={id}>
    <h3 className={classes.exampleTitle}>
      <a href={`#${id}`}>{title}</a>
    </h3>
    <div className={classes.exampleText}>{children}</div>
    <div className={classes.card}>
      {preview ? <div className={classes.preview}>{preview}</div> : null}
      {code}
    </div>
  </section>
);

Example.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  preview: PropTypes.node,
  code: PropTypes.node,
};

const Tabs = ({ tabs }) => {
  const [current, setCurrent] = useState(tabs[0].id);
  const tab = tabs.find((t) => t.id === current) || tabs[0];
  return (
    <div>
      <div className={classes.tabs} role="tablist" aria-label="Framework">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tab.id}
            className={t.id === tab.id ? classes.tabActive : ""}
            onClick={() => setCurrent(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab.blocks.map((block) => (
          <CodeBlock key={tab.id + block.label} {...block} />
        ))}
      </div>
    </div>
  );
};

Tabs.propTypes = { tabs: PropTypes.array.isRequired };

// ---- content -------------------------------------------------------------------

const NAV = [
  { group: "Start", items: [["choose", "Three ways to use an icon"]] },
  {
    group: "Web font",
    items: [
      ["font-setup", "Add the stylesheet"],
      ["font-sizes", "Sizes"],
      ["font-lists", "List bullets"],
      ["font-transform", "Rotate and flip"],
      ["font-animate", "Spin and pulse"],
      ["font-utilities", "Fixed width and borders"],
      ["font-duotone", "Duotone"],
      ["font-custom", "Your own font"],
    ],
  },
  {
    group: "SVG",
    items: [
      ["svg-inline", "Inline SVG"],
      ["svg-css", "In CSS"],
      ["svg-files", "SVG and PNG files"],
    ],
  },
  {
    group: "Components",
    items: [
      ["components", "React, Vue, Svelte, Angular"],
      ["components-duotone", "Duotone components"],
    ],
  },
];

const WAYS = [
  {
    href: "#font-setup",
    icon: "book",
    title: "Web font",
    text: "One stylesheet, then a class per icon. Best when you use many icons across a site.",
  },
  {
    href: "#svg-inline",
    icon: "color-picker",
    title: "SVG",
    text: "Copy or download any icon. No extra files; it takes on the text colour around it.",
  },
  {
    href: "#components",
    icon: "circuit",
    title: "Components",
    text: "Ready-made React, Vue, Svelte and Angular components, sized and coloured like text.",
  },
];

const componentTabs = (shape) => {
  const name = componentName(shape);
  const tag = "icofont-" + (shape.isDuotone ? "duotone-" : "") + shape.name;
  const block = (label, language, code) => ({ label, language, code, display: shorten(code) });
  return [
    {
      id: "react",
      label: "React",
      blocks: [
        block(`${name}.jsx`, "javascript", toJsx(shape)),
        {
          label: "Usage",
          language: "javascript",
          code: `import { ${name} } from "./${name}";\n\n<${name} style={{ fontSize: 24, color: "#7c3aed" }} />`,
        },
      ],
    },
    {
      id: "vue",
      label: "Vue",
      blocks: [
        block(`${name}.vue`, "html", toVue(shape)),
        {
          label: "Usage",
          language: "html",
          code: `<script setup>\nimport ${name} from "./${name}.vue";\n</script>\n\n<template>\n  <${name} style="font-size: 24px; color: #7c3aed" />\n</template>`,
        },
      ],
    },
    {
      id: "svelte",
      label: "Svelte",
      blocks: [
        block(`${name}.svelte`, "html", toSvelte(shape)),
        {
          label: "Usage",
          language: "html",
          code: `<script>\n  import ${name} from "./${name}.svelte";\n</script>\n\n<${name} style="font-size: 24px; color: #7c3aed" />`,
        },
      ],
    },
    {
      id: "angular",
      label: "Angular",
      blocks: [
        block(`${tag}.component.ts`, "typescript", toAngular(shape)),
        {
          label: "Usage",
          language: "html",
          code: `<!-- add ${name}Component to your component's imports -->\n<${tag} style="font-size: 24px; color: #7c3aed"></${tag}>`,
        },
      ],
    },
  ];
};

// The icon as a CSS mask, so it takes the colour behind it
const svgMask = (shape) => ({
  WebkitMask: `url("${toDataUrl(shape)}") center / contain no-repeat`,
  mask: `url("${toDataUrl(shape)}") center / contain no-repeat`,
});

export const Examples = () => {
  const homeSvg = toSvg(HOME);
  const dataUrl = toDataUrl(HOME);
  const cssCode = `/* Takes the text colour: the icon is a mask over a coloured box */
.icon-home {
  display: inline-block;
  width: 1em;
  height: 1em;
  background: currentColor;
  -webkit-mask: url("${dataUrl}") center / contain no-repeat;
  mask: url("${dataUrl}") center / contain no-repeat;
}

/* Or as a fixed-colour image */
.logo-home {
  background: url("${dataUrl}") center / contain no-repeat;
}`;

  return (
    <section className="main-body">
      <Helmet>
        <title>Examples | IcoFont</title>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://icofont.com/examples" />
        <meta property="og:site_name" content="IcoFont" />
        <meta property="og:image" content="https://icofont.com/images/og/icons.png" />
        <meta name="og:title" content="Examples | IcoFont" />
        <meta name="og:description" content={DESCRIPTION} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="IcoFont" />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image:src" content="https://icofont.com/images/og/icons.png" />
      </Helmet>
      <PageHeader label="Examples" title="Use icons as a font, SVG or component" />

      <div className="main">
        <div className="large__container">
          <div className={classes.layout}>
            <nav className={classes.toc} aria-label="On this page">
              <span className={classes.tocTitle}>On this page</span>
              {NAV.map(({ group, items }) => (
                <div key={group} className={classes.tocGroup}>
                  <span>{group}</span>
                  <ul>
                    {items.map(([id, label]) => (
                      <li key={id}>
                        <a href={`#${id}`}>{label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>

            <div className={classes.content}>
              <section className={classes.example} id="choose">
                <h3 className={classes.exampleTitle}>
                  <a href="#choose">Three ways to use an icon</a>
                </h3>
                <div className={classes.exampleText}>
                  <p>Pick the one that fits your project. Every icon works in all three.</p>
                </div>
                <div className={classes.ways}>
                  {WAYS.map((way) => (
                    <a key={way.title} href={way.href} className={classes.way}>
                      <span className={classes.wayIcon}>
                        <UiIcon name={way.icon} />
                      </span>
                      <strong>{way.title}</strong>
                      <span>{way.text}</span>
                    </a>
                  ))}
                </div>
              </section>

              <h2 className={classes.groupTitle}>Web font</h2>

              <Example
                id="font-setup"
                title="Add the stylesheet"
                preview={
                  <span className={classes.inlineDemo}>
                    <UiIcon name="home" /> Home
                  </span>
                }
                code={
                  <CodeBlock
                    label="HTML"
                    code={`<!-- once, in <head> -->\n<link rel="stylesheet" href="icofont/icofont.min.css">\n\n<!-- anywhere -->\n<i class="icofont-home"></i> Home`}
                  />
                }
              >
                <p>
                  Download the package, copy its <code>icofont</code> folder into your project and link the
                  stylesheet. Each icon is an <code>&lt;i&gt;</code> with the icon’s class.
                </p>
              </Example>

              <Example
                id="font-sizes"
                title="Sizes"
                preview={
                  <div className={classes.row}>
                    {["xs", "sm", "md", "lg", "2x", "3x", "4x", "5x"].map((size) => (
                      <figure key={size}>
                        <UiIcon name="dart" className={`icofont-${size}`} />
                        <figcaption>{size}</figcaption>
                      </figure>
                    ))}
                  </div>
                }
                code={
                  <CodeBlock
                    label="HTML"
                    code={`<i class="icofont-dart icofont-xs"></i> <!-- also sm, md, lg -->\n<i class="icofont-dart icofont-2x"></i> <!-- 2x to 10x -->\n<i class="icofont-dart icofont-5x"></i>`}
                  />
                }
              >
                <p>
                  Icons are text, so they follow <code>font-size</code>. Use <code>icofont-xs</code> to{" "}
                  <code>icofont-lg</code> for steps around the text size, or <code>icofont-2x</code> to{" "}
                  <code>icofont-10x</code> for multiples of it.
                </p>
              </Example>

              <Example
                id="font-lists"
                title="List bullets"
                preview={
                  <ul className={`icofont-ul ${classes.list}`}>
                    <li>
                      <UiIcon name="check" /> Aligned to a 24px grid
                    </li>
                    <li>
                      <UiIcon name="check" /> Free for personal and commercial use
                    </li>
                    <li>
                      <UiIcon name="check" /> Web font, SVG and components
                    </li>
                  </ul>
                }
                code={
                  <CodeBlock
                    label="HTML"
                    code={`<ul class="icofont-ul">\n  <li><i class="icofont-check"></i> Aligned to a 24px grid</li>\n  <li><i class="icofont-check"></i> Free for personal and commercial use</li>\n</ul>`}
                  />
                }
              >
                <p>
                  Add <code>icofont-ul</code> to a list to replace its bullets with icons.
                </p>
              </Example>

              <Example
                id="font-transform"
                title="Rotate and flip"
                preview={
                  <div className={classes.row}>
                    {[
                      ["arrow-right", "", "normal"],
                      ["arrow-right", "icofont-rotate-90", "rotate-90"],
                      ["arrow-right", "icofont-rotate-180", "rotate-180"],
                      ["arrow-right", "icofont-rotate-270", "rotate-270"],
                      ["airplane-alt", "icofont-flip-horizontal", "flip-horizontal"],
                      ["airplane-alt", "icofont-flip-vertical", "flip-vertical"],
                    ].map(([icon, cls, label]) => (
                      <figure key={label}>
                        <UiIcon name={icon} className={`icofont-2x ${cls}`} />
                        <figcaption>{label}</figcaption>
                      </figure>
                    ))}
                  </div>
                }
                code={
                  <CodeBlock
                    label="HTML"
                    code={`<i class="icofont-arrow-right icofont-rotate-90"></i>\n<i class="icofont-arrow-right icofont-rotate-180"></i>\n<i class="icofont-airplane-alt icofont-flip-horizontal"></i>\n<i class="icofont-airplane-alt icofont-flip-vertical"></i>`}
                  />
                }
              >
                <p>
                  Turn an icon in 90° steps with <code>icofont-rotate-*</code>, or mirror it with{" "}
                  <code>icofont-flip-horizontal</code> and <code>icofont-flip-vertical</code>.
                </p>
              </Example>

              <Example
                id="font-animate"
                title="Spin and pulse"
                preview={
                  <div className={classes.row}>
                    <figure>
                      <UiIcon name="spinner" className="icofont-2x icofont-spin" />
                      <figcaption>spin</figcaption>
                    </figure>
                    <figure>
                      <UiIcon name="spinner" className="icofont-2x icofont-pulse" />
                      <figcaption>pulse</figcaption>
                    </figure>
                  </div>
                }
                code={
                  <CodeBlock
                    label="HTML"
                    code={`<i class="icofont-spinner icofont-spin"></i>  <!-- smooth -->\n<i class="icofont-spinner icofont-pulse"></i> <!-- 8 steps per turn -->`}
                  />
                }
              >
                <p>For loading states. Both stop for people who turn on reduced motion.</p>
              </Example>

              <Example
                id="font-utilities"
                title="Fixed width and borders"
                preview={
                  <div className={classes.menuDemo}>
                    <ul>
                      {[
                        ["home", "Home"],
                        ["search", "Search"],
                        ["at", "Contact"],
                      ].map(([icon, label]) => (
                        <li key={label}>
                          <UiIcon name={icon} className="icofont-fw" /> {label}
                        </li>
                      ))}
                    </ul>
                    <p>
                      <UiIcon name="light-bulb" className="icofont-2x icofont-border" /> A tip with a framed
                      icon.
                    </p>
                  </div>
                }
                code={
                  <CodeBlock
                    label="HTML"
                    code={`<!-- same width for every icon, so labels line up -->\n<i class="icofont-home icofont-fw"></i> Home\n<i class="icofont-search icofont-fw"></i> Search\n\n<i class="icofont-light-bulb icofont-2x icofont-border"></i>`}
                  />
                }
              >
                <p>
                  <code>icofont-fw</code> gives every icon the same width, so menu labels line up.{" "}
                  <code>icofont-border</code> adds a light frame.
                </p>
              </Example>

              <Example
                id="font-duotone"
                title="Duotone"
                preview={
                  <div className={`${classes.row} ${classes.accent}`}>
                    {["duotone:add-users", "duotone:apply", "duotone:archive", "duotone:components", "duotone:comment"].map(
                      (icon) => (
                        <UiIcon key={icon} name={icon} className="icofont-3x" />
                      )
                    )}
                  </div>
                }
                code={
                  <CodeBlock
                    label="HTML"
                    code={`<i class="icofont-duotone icofont-add-users"></i>\n\n<!-- the second tone is the same colour at 60%; set the colour as usual -->\n<i class="icofont-duotone icofont-apply" style="color: #7c3aed"></i>`}
                  />
                }
              >
                <p>
                  Add <code>icofont-duotone</code> next to the icon class. Find them under{" "}
                  <strong>Style: Duotone</strong> on the icons page.
                </p>
              </Example>

              <Example
                id="font-custom"
                title="Your own font"
                preview={
                  <ol className={classes.steps}>
                    <li>
                      <span className={classes.stepIcon}>
                        <UiIcon name="plus" />
                      </span>
                      <strong>Pick</strong>
                      <span>Open the Composer and click the icons you need, or add a whole category.</span>
                    </li>
                    <li>
                      <span className={classes.stepIcon}>
                        <UiIcon name="options" />
                      </span>
                      <strong>Set it up</strong>
                      <span>Name the font, keep or change the class prefix, choose formats.</span>
                    </li>
                    <li>
                      <span className={classes.stepIcon}>
                        <UiIcon name="arrow-down" />
                      </span>
                      <strong>Download font</strong>
                      <span>A zip with only your icons: CSS, fonts and a demo page.</span>
                    </li>
                  </ol>
                }
                code={
                  <CodeBlock
                    label="Inside the zip"
                    language="bash"
                    code={`my-icofont/\n  my-icofont.css       # readable\n  my-icofont.min.css   # link this one\n  fonts/               # the formats you picked\n  demo.html            # every icon in your font`}
                  />
                }
              >
                <p>
                  Ship only the icons you use, built in the <Link to={COMPOSER_PATH}>Composer</Link>. Keep the{" "}
                  <code>icofont</code> prefix and class names stay the same as in the full set, so switching
                  needs no code changes.
                </p>
              </Example>

              <h2 className={classes.groupTitle}>SVG</h2>

              <Example
                id="svg-inline"
                title="Inline SVG"
                preview={
                  <div className={classes.row}>
                    {[
                      ["#171717", "1.5em"],
                      ["#7c3aed", "2em"],
                      ["#0e9f6e", "2.5em"],
                      ["#dc3545", "3em"],
                    ].map(([color, size]) => (
                      <span
                        key={color}
                        className={classes.svgDemo}
                        style={{ color, fontSize: size }}
                        dangerouslySetInnerHTML={{ __html: toSvg(HOME, { size: "1em" }) }}
                      />
                    ))}
                  </div>
                }
                code={<CodeBlock label="SVG" code={homeSvg} display={shorten(homeSvg)} />}
              >
                <p>
                  Open any icon and press <strong>Copy SVG</strong>. It’s drawn on a 24×24 grid and filled
                  with <code>currentColor</code>, so it takes the colour of the text around it. Change{" "}
                  <code>width</code> and <code>height</code> to resize it.
                </p>
              </Example>

              <Example
                id="svg-css"
                title="In CSS"
                preview={
                  <div className={classes.row}>
                    <button type="button" className="btn btn-primary" tabIndex={-1}>
                      <span className={classes.maskIcon} style={svgMask(HOME)} aria-hidden="true" />
                      Go home
                    </button>
                    <button type="button" className="btn btn-outline-primary" tabIndex={-1}>
                      <span className={classes.maskIcon} style={svgMask(HOME)} aria-hidden="true" />
                      Go home
                    </button>
                  </div>
                }
                code={
                  <CodeBlock
                    label="CSS"
                    language="css"
                    code={cssCode}
                    display={cssCode.replace(/data:image\/svg\+xml,[^"]+/g, "data:image/svg+xml,…")}
                  />
                }
              >
                <p>
                  Choose <strong>Copy Data URL</strong> in an icon’s menu. Used as a <code>mask</code>, the
                  icon follows the text colour, as in both buttons here; as a <code>background</code> it keeps
                  its own colour.
                </p>
              </Example>

              <Example
                id="svg-files"
                title="SVG and PNG files"
                preview={
                  <div className={classes.files}>
                    <div>
                      <UiIcon name="file-pdf" className="icofont-2x" />
                      <strong>Download SVG</strong>
                      <span>For design tools and build pipelines.</span>
                    </div>
                    <div>
                      <UiIcon name="color-picker" className="icofont-2x" />
                      <strong>Download PNG</strong>
                      <span>512×512 with a transparent background.</span>
                    </div>
                  </div>
                }
              >
                <p>
                  Both are in each icon’s menu, next to <strong>Copy SVG</strong>. Files are named after the
                  icon, like <code>icofont-home.svg</code>.
                </p>
              </Example>

              <h2 className={classes.groupTitle}>Components</h2>

              <Example
                id="components"
                title="React, Vue, Svelte, Angular"
                preview={
                  <span className={classes.inlineDemo} style={{ color: "#7c3aed", fontSize: 32 }}>
                    <UiIcon name="home" />
                  </span>
                }
                code={<Tabs tabs={componentTabs(HOME)} />}
              >
                <p>
                  Open any icon and pick your framework under <strong>Use in code</strong>. Each is one file
                  with no dependencies. Icons are <code>1em</code> and <code>currentColor</code>, so set{" "}
                  <code>font-size</code> and <code>color</code> to style them.
                </p>
              </Example>

              <Example
                id="components-duotone"
                title="Duotone components"
                preview={
                  <span className={`${classes.inlineDemo} ${classes.accent}`} style={{ fontSize: 40 }}>
                    <UiIcon name="duotone:add-users" />
                  </span>
                }
                code={<Tabs tabs={componentTabs(DUO)} />}
              >
                <p>Duotone icons export the same way. The second layer is the same colour at 60% opacity.</p>
              </Example>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Examples;
